import { Button, Text } from "north.js";
import React, { useState } from "react";
import styled from "styled-components";

import {
  HardwareRequestStatus,
  HardwareSku,
  HardwareSkuType,
} from "src/api/types.generated";
import { errorToast, successToast } from "src/shared/components";
import HardwareItemCard, {
  THardwareItemCardProps,
} from "src/shared/components/Hardware/ItemCard";
import globalConstants from "src/theme/globalConstants";

import { useRemoveItemsFromHardwareRequestMutation } from "../../../graphql/removeItemsFromHardwareRequest.generated";
import { useUpdateHardwareRequestItemQuantitiesMutation } from "../../../graphql/updateHardwareRequestItemQuantities.generated";
import { buildHardwarePrintPackage } from "../../../shared/printHardwareLabel";
import { usePrintHardwareLabel } from "../../../shared/usePrintHardwareLabel";

import OtherRequestsModal from "./OtherRequestsModal";

interface CurrentRequestCardProps {
  request: {
    id: number;
    status: HardwareRequestStatus;
    user_id: number;
    user: {
      name: string;
      email: string;
    };
    hardware_item_requests?: Array<{
      id: number;
      quantity: number;
      HardwareItem: {
        id: number;
        sku_code: string;
        total_quantity: number;
        available_quantity: number;
      };
    }>;
    created_at: string;
    requested_at?: string;
    accepted_at?: string;
    ready_at?: string;
    picked_up_at?: string;
    returned_at?: string;
    denied_at?: string;
    cancelled_at?: string;
  } | null;
  items: Pick<
    HardwareSku,
    | "code"
    | "image"
    | "type"
    | "name"
    | "location"
    | "description"
    | "category_id"
  >[];
  onAccept: () => void;
  onMarkReady: () => void;
  onSplitRequest: () => void;
  onCheckAvailability: () => void;
  onDeny: () => void;
  onNext: () => void;
  onPrevious: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  currentIndex: number;
  totalCount: number;
  isLoading: boolean;
  onRefetchRequest?: () => void;
}

const CurrentRequestCard: React.FC<CurrentRequestCardProps> = ({
  request,
  items,
  onAccept,
  onMarkReady,
  onSplitRequest,
  onCheckAvailability,
  onDeny,
  onNext,
  onPrevious,
  canGoNext,
  canGoPrevious,
  currentIndex,
  totalCount,
  isLoading,
  onRefetchRequest,
}) => {
  const { printingRequestId, printLabel } = usePrintHardwareLabel();
  const [isOtherRequestsModalOpen, setIsOtherRequestsModalOpen] =
    useState(false);
  const [updateHardwareRequestItemQuantities] =
    useUpdateHardwareRequestItemQuantitiesMutation();
  const [removeItemsFromHardwareRequest] =
    useRemoveItemsFromHardwareRequestMutation();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const handleQuantityIncrease = async (
    itemRequestId: number,
    currentQuantity: number
  ) => {
    if (!request) return;
    try {
      await updateHardwareRequestItemQuantities({
        variables: {
          request_id: request.id,
          updates: [
            {
              hardware_item_id: itemRequestId,
              new_quantity: currentQuantity + 1,
            },
          ],
        },
      });
      if (onRefetchRequest) {
        onRefetchRequest();
      }
    } catch (e) {
      errorToast((e as any)?.message || "Failed to update quantity");
    }
  };

  const handleQuantityDecrease = async (
    itemRequestId: number,
    currentQuantity: number
  ) => {
    if (!request) return;

    if (currentQuantity <= 1) {
      if ((request.hardware_item_requests?.length ?? 0) <= 1) {
        errorToast("Cannot remove the last item in a request");
      }
      return;
    }

    try {
      await updateHardwareRequestItemQuantities({
        variables: {
          request_id: request.id,
          updates: [
            {
              hardware_item_id: itemRequestId,
              new_quantity: currentQuantity - 1,
            },
          ],
        },
      });
      if (onRefetchRequest) {
        onRefetchRequest();
      }
    } catch (e) {
      errorToast((e as any)?.message || "Failed to update quantity");
    }
  };

  const handleRemoveItem = async (hardwareItemId: number) => {
    if (!request) return;

    if ((request.hardware_item_requests?.length ?? 0) <= 1) {
      errorToast("Cannot remove the last item in a request");
      return;
    }

    try {
      await removeItemsFromHardwareRequest({
        variables: {
          request_id: request.id,
          item_ids: [hardwareItemId],
        },
      });
      if (onRefetchRequest) {
        onRefetchRequest();
      }
      successToast("Item removed from request successfully");
    } catch (e) {
      errorToast((e as any)?.message || "Failed to remove item from request");
    }
  };

  // Check if there are any unavailable items (requested quantity > available quantity)
  const hasUnavailableItems =
    request?.hardware_item_requests?.some(
      (item) => item.quantity > item.HardwareItem.available_quantity
    ) || false;

  // Check if request contains any lottery items
  const hasLotteryItems =
    request?.hardware_item_requests?.some((item) => {
      const hardwareSku = items.find(
        (sku) => sku.code === item.HardwareItem.sku_code
      );
      return hardwareSku?.type === HardwareSkuType.Lottery;
    }) || false;

  const handlePrintLabel = async () => {
    if (!request) {
      errorToast("No request selected");
      return;
    }

    await printLabel(request.id, buildHardwarePrintPackage(request, items));
  };

  if (!request) {
    return (
      <Card>
        <CardHeader>
          <Text type="h2">Current Request</Text>
        </CardHeader>
        <EmptyState>
          <Text
            type="body1"
            style={{
              color: globalConstants?.color?.textSecondary as string,
            }}
          >
            No request currently being packed
          </Text>
        </EmptyState>
      </Card>
    );
  }

  const getStatusColor = (status: HardwareRequestStatus) => {
    switch (status) {
      case HardwareRequestStatus.Requested:
        return "#ff9800";
      case HardwareRequestStatus.Accepted:
        return "#4caf50";
      default:
        return "#9e9e9e";
    }
  };

  const getStatusText = (status: HardwareRequestStatus) => {
    switch (status) {
      case HardwareRequestStatus.Requested:
        return "Requested";
      case HardwareRequestStatus.Accepted:
        return "Accepted";
      default:
        return status;
    }
  };

  return (
    <Card>
      <CardHeader>
        <HeaderLeft>
          <Text type="h2">Current Request {hasLotteryItems && "⭐"}</Text>
          <RequestCounter>
            {currentIndex} of {totalCount}
          </RequestCounter>
        </HeaderLeft>
        <HeaderRight>
          <NavigationButtons>
            <Button
              color="secondary"
              size="sm"
              onClick={onPrevious}
              disabled={!canGoPrevious || isLoading}
            >
              ← Previous
            </Button>
            <Button
              color="secondary"
              size="sm"
              onClick={onNext}
              disabled={!canGoNext || isLoading}
            >
              Next →
            </Button>
          </NavigationButtons>
          <StatusBadge color={getStatusColor(request.status)}>
            {getStatusText(request.status)}
          </StatusBadge>
        </HeaderRight>
      </CardHeader>

      <RequestInfo>
        <InfoRow>
          <Text type="body2">Request ID:</Text>
          <Text type="body2" style={{ fontWeight: 600 }}>
            #{request.id}
          </Text>
        </InfoRow>
        <InfoRow>
          <Text type="body2">User:</Text>
          <Text type="body2" style={{ fontWeight: 600 }}>
            {request.user.name}
          </Text>
        </InfoRow>
        <InfoRow>
          <Text type="body2">Email:</Text>
          <Text type="body2" style={{ fontWeight: 600 }}>
            {request.user.email}
          </Text>
        </InfoRow>
        <InfoRow>
          <Text type="body2">Items:</Text>
          <Text type="body2" style={{ fontWeight: 600 }}>
            {request.hardware_item_requests?.length || 0} item(s)
          </Text>
        </InfoRow>
        <InfoRow>
          <Text type="body2">Created:</Text>
          <Text type="body2" style={{ fontWeight: 600 }}>
            {new Date(request.created_at).toLocaleString()}
          </Text>
        </InfoRow>
      </RequestInfo>

      {(request.requested_at ||
        request.accepted_at ||
        request.ready_at ||
        request.picked_up_at ||
        request.returned_at ||
        request.denied_at ||
        request.cancelled_at) && (
        <TimelineSection>
          <Text
            type="body2"
            style={{
              color: globalConstants?.color?.textSecondary as string,
              fontWeight: 600,
              marginBottom: "8px",
            }}
          >
            Request Timeline:
          </Text>
          <TimelineGrid>
            {request.requested_at && (
              <TimelineItem>
                <Text
                  type="body2"
                  style={{
                    color: globalConstants?.color?.textSecondary as string,
                    minWidth: "80px",
                  }}
                >
                  Requested:
                </Text>
                <Text type="body2" style={{ fontWeight: 600 }}>
                  {formatDate(request.requested_at)}
                </Text>
              </TimelineItem>
            )}
            {request.accepted_at && (
              <TimelineItem>
                <Text
                  type="body2"
                  style={{
                    color: globalConstants?.color?.textSecondary as string,
                    minWidth: "80px",
                  }}
                >
                  Accepted:
                </Text>
                <Text type="body2" style={{ fontWeight: 600 }}>
                  {formatDate(request.accepted_at)}
                </Text>
              </TimelineItem>
            )}
            {request.ready_at && (
              <TimelineItem>
                <Text
                  type="body2"
                  style={{
                    color: globalConstants?.color?.textSecondary as string,
                    minWidth: "80px",
                  }}
                >
                  Ready:
                </Text>
                <Text type="body2" style={{ fontWeight: 600 }}>
                  {formatDate(request.ready_at)}
                </Text>
              </TimelineItem>
            )}
            {request.picked_up_at && (
              <TimelineItem>
                <Text
                  type="body2"
                  style={{
                    color: globalConstants?.color?.textSecondary as string,
                    minWidth: "80px",
                  }}
                >
                  Picked Up:
                </Text>
                <Text type="body2" style={{ fontWeight: 600 }}>
                  {formatDate(request.picked_up_at)}
                </Text>
              </TimelineItem>
            )}
            {request.returned_at && (
              <TimelineItem>
                <Text
                  type="body2"
                  style={{
                    color: globalConstants?.color?.textSecondary as string,
                    minWidth: "80px",
                  }}
                >
                  Returned:
                </Text>
                <Text type="body2" style={{ fontWeight: 600 }}>
                  {formatDate(request.returned_at)}
                </Text>
              </TimelineItem>
            )}
            {request.denied_at && (
              <TimelineItem>
                <Text
                  type="body2"
                  style={{
                    color: globalConstants?.color?.textSecondary as string,
                    minWidth: "80px",
                  }}
                >
                  Denied:
                </Text>
                <Text type="body2" style={{ fontWeight: 600 }}>
                  {formatDate(request.denied_at)}
                </Text>
              </TimelineItem>
            )}
            {request.cancelled_at && (
              <TimelineItem>
                <Text
                  type="body2"
                  style={{
                    color: globalConstants?.color?.textSecondary as string,
                    minWidth: "80px",
                  }}
                >
                  Cancelled:
                </Text>
                <Text type="body2" style={{ fontWeight: 600 }}>
                  {formatDate(request.cancelled_at)}
                </Text>
              </TimelineItem>
            )}
          </TimelineGrid>
        </TimelineSection>
      )}

      <ItemsList>
        <Text type="h3">Items in Request</Text>
        {request.hardware_item_requests?.map((item) => {
          // Find the hardware SKU details using the SKU code
          const hardwareSku = items.find(
            (sku) => sku.code === item.HardwareItem.sku_code
          );

          if (!hardwareSku) {
            return (
              <Text
                key={item.id}
                type="body2"
                style={{
                  color: globalConstants?.color?.textSecondary as string,
                }}
              >
                SKU {item.HardwareItem.sku_code} not found
              </Text>
            );
          }

          // Create quantity management buttons for REQUESTED status
          const quantityButtons =
            request.status === HardwareRequestStatus.Requested
              ? [
                  {
                    text: "-",
                    onClick: () =>
                      handleQuantityDecrease(
                        item.HardwareItem.id,
                        item.quantity
                      ),
                    disabled: item.quantity <= 1 || isLoading,
                    color: "secondary" as const,
                  },
                  {
                    text: "+",
                    onClick: () =>
                      handleQuantityIncrease(
                        item.HardwareItem.id,
                        item.quantity
                      ),
                    disabled: isLoading,
                    color: "secondary" as const,
                  },
                  {
                    text: "Remove",
                    onClick: () => handleRemoveItem(item.HardwareItem.id),
                    disabled: isLoading,
                    color: "danger" as const,
                  },
                ]
              : undefined;

          // Create HardwareItemCard props similar to processRequests
          const cardProps: THardwareItemCardProps = {
            skuCode: hardwareSku.code,
            requestId: request.id,
            image: hardwareSku.image,
            status: undefined, // Don't show status on individual items
            title: hardwareSku.name,
            description: hardwareSku.location
              ? `Location: ${hardwareSku.location}`
              : undefined, // Show location instead of description
            quantity: item.quantity,
            submitted_by: undefined, // Don't show submitted_by on individual items
            timestamp: request.created_at,
            requested_at: request.requested_at,
            accepted_at: request.accepted_at,
            ready_at: request.ready_at,
            picked_up_at: request.picked_up_at,
            returned_at: request.returned_at,
            denied_at: request.denied_at,
            cancelled_at: request.cancelled_at,
            showAllTimestamps: false, // Don't show timestamps on individual items
            buttons: quantityButtons, // Add quantity management buttons for REQUESTED status
            onClick: undefined,
            showRemainingQuantity: true, // Show remaining quantity for packing
            remainingQuantity: item.HardwareItem.available_quantity,
          };

          return <HardwareItemCard key={item.id} {...cardProps} />;
        }) || (
          <Text
            type="body2"
            style={{ color: globalConstants?.color?.textSecondary as string }}
          >
            No items in this request
          </Text>
        )}
      </ItemsList>

      <ActionButtons>
        <Button
          color="secondary"
          size="md"
          onClick={() => setIsOtherRequestsModalOpen(true)}
        >
          View User&apos;s Requests
        </Button>
        {request.status === HardwareRequestStatus.Requested && (
          <>
            <Button
              color="secondary"
              size="md"
              onClick={onCheckAvailability}
              disabled={isLoading}
            >
              Check Availability
            </Button>
            {hasUnavailableItems && (
              <Button
                color="secondary"
                size="md"
                onClick={onSplitRequest}
                disabled={isLoading}
              >
                Split Request
              </Button>
            )}
            <Button
              color="primary"
              size="md"
              onClick={onAccept}
              disabled={isLoading || hasUnavailableItems}
            >
              Accept Request
            </Button>
            <Button
              color="danger"
              size="md"
              onClick={onDeny}
              disabled={isLoading}
            >
              Deny Request
            </Button>
          </>
        )}
        {request.status === HardwareRequestStatus.Accepted && (
          <Button
            color="primary"
            size="md"
            onClick={onMarkReady}
            disabled={isLoading}
          >
            Mark as Ready
          </Button>
        )}
        {(request.status === HardwareRequestStatus.Accepted ||
          request.status === HardwareRequestStatus.Ready) && (
          <Button
            color="secondary"
            size="md"
            onClick={handlePrintLabel}
            disabled={isLoading || printingRequestId === request.id}
          >
            {printingRequestId === request.id ? "Printing..." : "Print Label"}
          </Button>
        )}
      </ActionButtons>

      <OtherRequestsModal
        isOpen={isOtherRequestsModalOpen}
        onClose={() => setIsOtherRequestsModalOpen(false)}
        userId={request.user_id}
        userName={request.user.name}
        currentRequestId={request.id}
        items={items}
      />
    </Card>
  );
};

export default CurrentRequestCard;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const RequestCounter = styled.div`
  font-size: 14px;
  color: ${({ theme }) =>
    theme.globalConstants?.color?.textSecondary || "#666"};
  font-weight: 500;
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const StatusBadge = styled.div<{ color: string }>`
  padding: 6px 12px;
  border-radius: 16px;
  background-color: ${({ color }) => color}20;
  color: ${({ color }) => color};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
`;

const RequestInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
`;

const TimelineSection = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.textLight};
`;

const TimelineGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
`;
